import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, DialogActions, DialogContent, DialogTitle, Divider, FormControl, FormControlLabel, FormHelperText, Switch, LinearProgress, List, ListItem, Tooltip } from "@mui/material";
import useSpaceTopics, { validTopic, validTopicSettings } from "../hooks/useSpaceTopics";
import { api } from "../utils/helpers";
import { HomeButton, HomeDialog, HomeInput } from "./SpacesHomeControls";
import "./TransportTopics.css";

export default function SpaceTopicsEditor({ spaceId }) {
  return <ScopedSpaceTopicsEditor key={spaceId} spaceId={spaceId}/>;
}

function ScopedSpaceTopicsEditor({ spaceId }) {
  const { t } = useTranslation("freigabe");
  const data = useSpaceTopics(spaceId);
  const [name, setName] = useState("");
  const [editing, setEditing] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState("");
  const alive = useRef(true);
  const writing = useRef(false);
  const nameInput = useRef(null);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  const ready = data.canManage && !data.loading && !data.error && !busy;
  const removalCurrent = removing && data.topics.some(topic => topic.id === removing.id
    && topic.revision === removing.revision && topic.memberCount === 0);
  const mutate = async (topic, changes, remove = false) => {
    if (!ready || writing.current) return;
    if (remove && !removalCurrent) return;
    writing.current = true; setBusy(true); setError(null); setSuccess("");
    try {
      const result = await api(topic ? `/api/cicd/topics/${encodeURIComponent(topic.id)}` : "/api/cicd/topics", {
        method: remove ? "DELETE" : topic ? "PATCH" : "POST", body: JSON.stringify({ spaceId, ...(topic ? { revision: topic.revision } : {}), ...changes }),
      });
      if (!validTopic(result?.topic)) throw new Error("TOPIC_RESPONSE_INVALID");
      if (remove && (result.topic.id !== topic.id || !result.topic.deletedAt)) throw new Error("TOPIC_RESPONSE_INVALID");
      if (!alive.current) return;
      setEditing(null); setRemoving(null); setName(""); setSuccess(t(remove ? "topics.topicDeleted" : "topics.saved"));
    } catch (failure) { if (alive.current) setError(failure); }
    finally {
      writing.current = false;
      if (alive.current) { await data.reload(); if (alive.current) setBusy(false); }
    }
  };
  const activeTopics = data.topics.filter(topic => !topic.archived && topic.selectable !== false);
  const setRequired = async required => {
    if (!ready || writing.current || !data.settings || (required && !activeTopics.length)) return;
    writing.current = true; setBusy(true); setError(null); setSuccess("");
    try {
      const response = await api("/api/cicd/topics/settings", { method: "PATCH", body: JSON.stringify({ spaceId,
        requireTopicSelection: required, revision: data.settings.revision }) });
      if (response?.spaceId !== spaceId || !validTopicSettings(response?.settings) || response.settings.requireTopicSelection !== required) throw Object.assign(new Error("TOPIC_RESPONSE_INVALID"), { code: "TOPIC_RESPONSE_INVALID" });
      if (alive.current) setSuccess(t("topics.saved"));
    } catch (failure) { if (alive.current) setError(failure); }
    finally { writing.current = false; if (alive.current) { await data.reload(); if (alive.current) setBusy(false); } }
  };
  return <section className="topic-ui topic-editor" aria-labelledby="space-topics-title" aria-busy={busy || data.loading}>
    <h3 id="space-topics-title">{t("topics.title")}</h3>
    <p>{t("topics.editorHint")}</p>
    {data.loading && <LinearProgress aria-label={t("topics.loading")}/>}
    {data.error && <Alert severity="error" action={<HomeButton disabled={busy || data.loading} onClick={data.reload}>{t("topics.reload")}</HomeButton>}>{t("topics.readError")}</Alert>}
    {error && !removing && <Alert severity="error">{t(`topics.errors.${error.code}`, { defaultValue: t("topics.writeError") })}</Alert>}
    {success && <p role="status">{success}</p>}
    <FormControl className="topic-required-setting">
      <FormControlLabel label={t("topics.requiredSetting")} control={<Switch checked={data.settings?.requireTopicSelection === true}
        disabled={!ready || !data.settings || (!data.settings.requireTopicSelection && !activeTopics.length)}
        onChange={event => setRequired(event.target.checked)} inputProps={{ "aria-describedby": "topic-required-hint" }}/>}/>
      <FormHelperText id="topic-required-hint">{!data.loading && !data.error && !activeTopics.length ? t("topics.requiredNoActive") : t("topics.requiredSettingHint")}</FormHelperText>
    </FormControl>
    {data.canManage && <div className="topic-editor-form">
      <FormControl fullWidth><label htmlFor="space-topic-name">{t(editing ? "topics.renameLabel" : "topics.newLabel")}</label>
        <HomeInput ref={nameInput} id="space-topic-name" value={name} disabled={!ready} maxLength={120} onChange={event => setName(event.target.value)}/>
      </FormControl>
      <div className="topic-actions"><HomeButton className="iio-btn--signal" disabled={!ready || !name.trim()} onClick={() => mutate(editing, { name: name.trim() })}>
        {t(editing ? "topics.saveName" : "topics.create")}</HomeButton>
        {editing && <HomeButton disabled={busy} onClick={() => { setEditing(null); setName(""); }}>{t("topics.cancel")}</HomeButton>}</div>
    </div>}
    <Divider className="topic-editor-divider"/>
    <h4>{t("topics.manage")}</h4>
    {!data.loading && !data.error && !data.topics.length && <p>{t("topics.empty")}</p>}
    <List disablePadding>{data.topics.map(topic => <ListItem disableGutters className="topic-editor-row" key={topic.id}>
      <div><strong>{topic.name}</strong><div>{t("topics.members", { count: topic.memberCount || 0 })}{topic.archived ? ` · ${t("topics.archived")}` : ""}</div></div>
      {data.canManage && <div className="topic-actions">
        <HomeButton disabled={!ready} onClick={() => { setEditing(topic); setName(topic.name); requestAnimationFrame(() => nameInput.current?.focus()); }}>{t("topics.rename")}</HomeButton>
        <HomeButton disabled={!ready} onClick={() => mutate(topic, { archived: !topic.archived })}>{t(topic.archived ? "topics.restore" : "topics.archive")}</HomeButton>
        <Tooltip describeChild title={topic.memberCount > 0 ? t("topics.errors.TOPIC_NOT_EMPTY") : ""}>
          <span tabIndex={topic.memberCount > 0 ? 0 : undefined}><HomeButton className="iio-btn--danger" disabled={!ready || topic.memberCount !== 0}
            onClick={() => { setRemoving(topic); setError(null); setSuccess(""); }}>{t("topics.deleteTopic")}</HomeButton></span>
        </Tooltip>
      </div>}
    </ListItem>)}</List>
    {removing && <HomeDialog onClose={() => { if (!busy) setRemoving(null); }} aria-labelledby="topic-delete-title" aria-describedby="topic-delete-hint">
      <DialogTitle id="topic-delete-title">{t("topics.deleteTopic")}</DialogTitle>
      <DialogContent>
        <p id="topic-delete-hint">{t("topics.deleteTopicHint", { name: removing.name })}</p>
        {error && <Alert severity="error">{t(`topics.errors.${error.code}`, { defaultValue: t("topics.writeError") })}</Alert>}
        {data.error && <Alert severity="error">{t("topics.readError")}</Alert>}
        {!data.loading && !data.error && !removalCurrent && <Alert severity="warning">{t("topics.changed")}</Alert>}
      </DialogContent>
      <DialogActions>
        <HomeButton autoFocus disabled={busy} onClick={() => setRemoving(null)}>{t("topics.cancel")}</HomeButton>
        <HomeButton className="iio-btn--danger" disabled={!ready || !removalCurrent} onClick={() => mutate(removing, {}, true)}>{t("topics.deleteTopic")}</HomeButton>
      </DialogActions>
    </HomeDialog>}
  </section>;
}
